import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AgGridAtom, ModalAtom } from "../../../components/atoms/atom";
import { FilterBar } from "../../../components/molecule/molecule";
import { ACCESS_RIGHT_AREAS } from "../../../constant/permissions";
import {
    clearError,
    createTemplate,
    deleteTemplate,
    fetchTemplate,
    fetchTemplates,
    updateTemplate
} from "../../../store/slices/templateSlice";
import { getSearchAddFilter } from "../../../utils/get-filter-items";
import { showToast } from "../../../utils/method";
import { searchData } from "../../../utils/search";
import AddTemplateModal from "./add/add-template";
import { getTemplateTableColumns } from "./constant.js";
import "./template.scss";
import TableSkeleton from "../../../components/atoms/table-skeleton/TableSkeleton";

function TemplateSettingPage() {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Get template data from Redux store
    const {
        templates,
        loading,
        error
    } = useSelector((state) => state.templates);

    // Get current user for permission checks
    const { user: currentUser } = useSelector((state) => state.auth);

    const [templateModalView, setTemplateModalView] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedTemplateData, setSelectedTemplateData] = useState({});
    const [filteredTemplateList, setFilteredTemplateList] = useState([]);

    const [filterItems, setFilterItems] = useState(
        getSearchAddFilter({ addTextKey: "templates.add-template" })
    );

    // Fetch templates when component mounts
    useEffect(() => {
        dispatch(fetchTemplates())
            .unwrap()
            .catch(error => {
                showToast(error, { type: "error" });
            });
    }, [dispatch]);

    // Update filtered templates when templates list changes
    useEffect(() => {
        setFilteredTemplateList(templates);
    }, [templates]);

    // Show error toast when error occurs
    useEffect(() => {
        if (error) {
            showToast(error, { type: "error" });
            dispatch(clearError());
        }
    }, [error, dispatch]);

    // Handle template edit
    const onTemplateEdit = async (row) => {
        try {
            // Fetch the complete template data including files
            const templateData = await dispatch(fetchTemplate(row.id)).unwrap();
            setSelectedTemplateData(templateData);
            setTemplateModalView("edit");
        } catch (error) {
            showToast(error || t("errors.general"), { type: "error" });
        }
    };

    // Handle template delete
    const onTemplateDelete = (row) => {
        setSelectedTemplateData(row);
        setShowDeleteModal(true);
    };

    // Confirm template deletion
    const onDeleteTemplate = async () => {
        try {
            await dispatch(deleteTemplate(selectedTemplateData?.id)).unwrap();
            setShowDeleteModal(false);
            showToast(t("templates.delete-success"), { type: "success" });
        } catch (error) {
            showToast(error || t("errors.general"), { type: "error" });
        }
    };

    // Handle template create/update
    const onAddTemplate = async (templateInfo) => {
        try {
            if (templateModalView === "add") {
                await dispatch(createTemplate(templateInfo)).unwrap();
                showToast(t("templates.save-success"), { type: "success" });
            } else if (templateModalView === "edit") {
                await dispatch(updateTemplate({
                    templateId: selectedTemplateData.id,
                    templateData: templateInfo
                })).unwrap();
                showToast(t("templates.update-success"), { type: "success" });
            }
            setTemplateModalView("");
        } catch (error) {
            showToast(error || t("errors.general"), { type: "error" });
        }
    };

    // Get template columns with current user ID
    const templateColumns = getTemplateTableColumns(
        t,
        onTemplateEdit,
        onTemplateDelete,
        currentUser?.id
    );

    // Handle filter actions
    const onFilterAction = (index, key, info) => {
        const localFilterItems = [...filterItems];
        localFilterItems[index].value = info?.value;
        setFilterItems(localFilterItems);

        let filtered = [...templates];

        // Apply all active filters
        localFilterItems.forEach((item) => {
            if (item.key === "search" && item.value) {
                filtered = searchData(
                    item.value,
                    filtered,
                    templateColumns.slice(0, -1)
                );
            }
        });

        setFilteredTemplateList(filtered);

        // Handle add action separately
        if (key === "add") {
            setSelectedTemplateData({});
            setTemplateModalView("add");
        }
    };

    return (
        <div className="page-container">
            <FilterBar
                items={filterItems}
                onFilterAction={onFilterAction}
                area={ACCESS_RIGHT_AREAS.TEMPLATE}
            />

            <div className="flex-grow-1 ag-grid-parent">
                {loading.templates && filteredTemplateList.length === 0 ? (
                    <TableSkeleton />
                ) : (
                    <AgGridAtom
                        columnDefs={templateColumns}
                        rowData={filteredTemplateList}
                        context={{
                            permissions: currentUser?.permissions || [],
                            currentUserId: currentUser?.id,
                            area: ACCESS_RIGHT_AREAS.TEMPLATE,
                            userPermissions: currentUser?.permissions || []
                        }}
                        enableBrowserTooltips
                        overlayLoadingTemplate=""
                        overlayNoRowsTemplate={`<span class="ag-overlay-no-rows-center">${templates.length === 0 ? t("templates.no-data") : t("common.no-results-found")}</span>`}
                        defaultColDef={{
                            flex: 1,
                            sortable: true,
                            filter: false,
                        }}
                    />
                )}
            </div>

            {/* Add/Edit Template Modal */}
            {templateModalView && (
                <AddTemplateModal
                    mode={templateModalView}
                    templateInfo={selectedTemplateData}
                    show
                    onClose={() => {
                        setTemplateModalView("");
                        setSelectedTemplateData({});
                    }}
                    onSubmit={onAddTemplate}
                    loading={loading.create || loading.update}
                />
            )}

            {/* Delete Confirmation Modal */}
            <ModalAtom
                title={t("templates.delete-template")}
                body={<p>{t("templates.delete-template-warning")}</p>}
                saveText={loading.delete ? t("common.deleting") : t("delete")}
                show={showDeleteModal}
                handleClose={() => setShowDeleteModal(false)}
                handleSubmit={onDeleteTemplate}
                disabled={loading.delete}
            />
        </div>
    );
}

export default TemplateSettingPage;